import { track } from "@/lib/tracking";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, MailCheck, Radio, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { INDUSTRIES, STATES } from "@/lib/constants";
import { TEASER_TEXT, INDUSTRY_ES } from "@/lib/landing_text";
import { useLandingLang, grantsCta } from "@/components/FundingCaseStudy";

function LeadForm({ r, T, lang }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const acq = JSON.parse(localStorage.getItem("gv_acq") || "null");
    try {
      await api.post("/public/teaser-lead", { email, industry: r.industry, state: r.state, lang, campaign: acq?.campaign || "", source: acq?.source || "", ad: acq?.ad || "" });
      track("Subscribe");
      setSent(email);
    } catch (e2) { setErr(e2?.response?.status === 429 ? formatErr(e2) : T.leadErr); } finally { setBusy(false); }
  };
  if (sent) return <p className="mt-4 text-sm text-emerald-300 flex items-center gap-2" data-testid="teaser-lead-success"><MailCheck className="h-4 w-4" />{T.leadOk(sent)}</p>;
  return (
    <form onSubmit={submit} className="mt-4 bg-slate-800/60 border border-slate-700 rounded-md p-3" data-testid="teaser-lead-form">
      <div className="text-xs font-semibold text-slate-200 mb-2">{T.leadTitle}</div>
      <div className="flex gap-2">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={T.leadPh} className="bg-slate-900 border-slate-600 text-white h-9" data-testid="teaser-lead-email-input" />
        <Button type="submit" disabled={busy} className="h-9 bg-white text-slate-950 hover:bg-emerald-100" data-testid="teaser-lead-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" />{T.leadBtn}</>}</Button>
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5">{T.leadNote}</p>
      {err && <p className="text-xs text-red-400 mt-1" data-testid="teaser-lead-error">{err}</p>}
    </form>
  );
}

function Result({ r, T, es, user }) {
  const generic = r.industry === "Nonprofit" || r.industry === "Small Business";
  const ind = generic ? "" : es ? INDUSTRY_ES[r.industry] : r.industry;
  const [n, rest] = T.result(r.count, ind, T.who[r.eligibility]);
  return (
    <div className="mt-4 pt-4 border-t border-slate-700" data-testid="teaser-result">
      <div className="flex items-baseline gap-2 flex-wrap"><span className="font-display text-4xl font-extrabold text-emerald-400" data-testid="teaser-count">{n}</span><span className="text-sm text-slate-300">{rest}</span></div>
      {r.samples.length > 0 && <ul className="mt-3 space-y-1.5" data-testid="teaser-samples">{r.samples.map((s, i) => <li key={i} className="text-xs text-slate-400 truncate">• {s.title} <span className="text-slate-500">— {s.agency}</span></li>)}</ul>}
      <p className="mt-3 text-xs text-slate-400">{r.state ? T.stateNote(r.state) : T.noState}</p>
      <Link to={grantsCta(user)} className="inline-block mt-3 text-sm font-semibold text-emerald-400 hover:text-emerald-300" data-testid="teaser-unlock-link">{user ? T.unlockIn : T.unlock} →</Link>
      {!user && <LeadForm r={r} T={T} lang={es ? "es" : "en"} />}
    </div>
  );
}

export function GrantTeaser() {
  const { user } = useAuth();
  const lang = useLandingLang();
  const es = lang === "es";
  const T = TEASER_TEXT[lang];
  const [industry, setIndustry] = useState("");
  const [state, setState] = useState("");
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  const check = async () => {
    setBusy(true); setErr(false);
    try { setR((await api.get("/public/grant-count", { params: { industry, state } })).data); } catch { setErr(true); setR(null); } finally { setBusy(false); }
  };
  return (
    <div className="bg-slate-900 border border-emerald-700/60 rounded-lg p-6" data-testid="grant-teaser">
      <div className="flex items-center justify-between gap-2"><h4 className="font-display text-lg font-bold text-white">{T.title}</h4><span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400"><Radio className="h-3 w-3" />{T.live}</span></div>
      <p className="text-sm text-slate-400 mt-1">{T.sub}</p>
      <div className="mt-4 grid sm:grid-cols-12 gap-2">
        <div className="sm:col-span-6"><Select value={industry} onValueChange={setIndustry}><SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="teaser-industry-select"><SelectValue placeholder={T.industry} /></SelectTrigger><SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{es ? INDUSTRY_ES[i] : i}</SelectItem>)}</SelectContent></Select></div>
        <div className="sm:col-span-6"><Select value={state} onValueChange={setState}><SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="teaser-state-select"><SelectValue placeholder={T.state} /></SelectTrigger><SelectContent className="max-h-72">{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        <Button onClick={check} disabled={!industry || busy} className="sm:col-span-12 bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="teaser-check-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" />{T.check}</>}</Button>
      </div>
      {err && <p className="text-sm text-red-400 mt-3" data-testid="teaser-error">{T.error}</p>}
      {r && <Result r={r} T={T} es={es} user={user} />}
    </div>
  );
}
